import React from "react";
import { Page, View } from "@react-pdf/renderer";
import type { PrintTemplate } from "@repo/types";
import {
  PDFHeader,
  PDFInfoField,
  PDFSectionHeader,
  PdfEssenceIllustrationPage,
  pdfStyles,
} from "@repo/print-pdf/shared";
import { imageService } from "@repo/ui/lib/services";
import type { PrintDataFields } from "../../print/hooks/use-print-data";
import { ARBRE_FIELDS, ARBRE_SECTIONS } from "../../print/template/registry";
import { resolveLayout } from "../../print/template/resolve-layout";
import type { ResolvedItem } from "../../print/template/types";
import {
  CommentairesBlock,
  DiagnosticSanitaireBlock,
  DiagnosticVisuelBlock,
} from "../blocks";

export interface PdfPrintTemplateProps {
  template: PrintTemplate;
  data: PrintDataFields;
  inventaireId: number;
}

const BLOCKS: Record<string, React.FC<{ data: PrintDataFields }>> = {
  bloc_diagnostic_visuel: DiagnosticVisuelBlock,
  bloc_diagnostic_sanitaire: DiagnosticSanitaireBlock,
  bloc_commentaires: CommentairesBlock,
};

const colorStyle = (backgroundColor?: string, color?: string) => ({
  ...(backgroundColor ? { backgroundColor } : {}),
  ...(color ? { color } : {}),
});

function renderItem(item: ResolvedItem, data: PrintDataFields) {
  if (item.kind === "block") {
    const Block = BLOCKS[item.key];
    return Block ? <Block key={item.key} data={data} /> : null;
  }
  return <PDFInfoField key={item.key} label={item.label} value={item.value} />;
}

/**
 * Peintre PDF : met en page le résultat de `resolveLayout` avec react-pdf (aucune décision de contenu ici).
 * Retourne la fiche (une `<Page>`, suivie de la page d'illustration de l'essence comme dans le PDF actuel) : à placer dans un `<Document>`. Une section reste entière sur une page (`wrap={false}`).
 */
export function PdfPrintTemplate({
  template,
  data,
  inventaireId,
}: PdfPrintTemplateProps) {
  const sections = resolveLayout(
    template,
    ARBRE_SECTIONS,
    ARBRE_FIELDS,
    data,
    "pdf",
  );
  const { header } = template;

  return (
    <>
      <Page size="A4" style={pdfStyles.page}>
        <PDFHeader
          organizationName={data.organizationName}
          organizationAddress={data.organizationAddress}
          organizationCity={data.organizationCity}
          organizationZipCode={data.organizationZipCode}
          organizationSiret={
            header.showSiret ? data.organizationSiret : undefined
          }
          organizationLogo={data.organizationLogo}
          user={header.showRecenseur ? data.user : undefined}
          showLogo={header.showLogo}
          showOrganisation={header.showOrganisation}
          logoSize="default"
        />

        <PDFSectionHeader
          title={`FICHE DE SYNTHESE ARBRE - ${inventaireId}`}
          variant="primary"
          style={colorStyle(header.titleBg, header.titleColor)}
        />

        {sections.map((section) => (
          <View key={section.id} wrap={false}>
            {section.title ? (
              <PDFSectionHeader
                title={section.title}
                variant="secondary"
                style={colorStyle(
                  section.style?.titleBg,
                  section.style?.titleColor,
                )}
              />
            ) : null}
            <View
              style={[
                pdfStyles.grid3,
                section.style?.background
                  ? { backgroundColor: section.style.background }
                  : {},
              ]}
            >
              {section.columns.map((column, index) => (
                <View
                  key={index}
                  style={
                    section.id === "identification" ||
                    column.every((item) => item.kind === "block")
                      ? pdfStyles.columnNoLeft
                      : pdfStyles.column
                  }
                >
                  {column.map((item) => renderItem(item, data))}
                </View>
              ))}
            </View>
          </View>
        ))}
      </Page>
      <PdfEssenceIllustrationPage
        latinName={data.especeLatin?.trim() || data.espece?.name || "Essence"}
        frenchName={
          data.especeLabel?.trim() ||
          data.espece?.nomFr ||
          data.espece?.nom_fr ||
          null
        }
        imageUrl={
          data.espece?.img_url
            ? imageService.getOptimizedImageUrl(
                data.espece.img_url,
                "espece",
                800,
                600,
              )
            : null
        }
      />
    </>
  );
}
